import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Collapse, Table, Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Badge, Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import FilterBar from "../../components/FilterBar";

const statusBadgeVariant = (status) => {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "failed":
    case "chargeback":
      return "error";
    case "cancelled":
    case "refunded":
      return "neutral";
    default:
      return "info";
  }
};

/**
 * Unsettled overdrafts, shown above the payment log. A debt is the reason a payment is about to
 * be taken, so it belongs on this page rather than only on each client's subscription — and an
 * overdue client has a blocked API, which nobody should have to go hunting for.
 */
const OutstandingBalances = ({ rows }) => {
  if (!rows.length) return null;

  const overdue = rows.filter((r) => r.isOverdue).length;
  const totalTokens = rows.reduce((sum, r) => sum + (r.outstanding || 0), 0);
  const totalAmount = rows.reduce((sum, r) => sum + Number(r.estimatedAmount || 0), 0);
  const currency = rows[0]?.currencyCode || "EUR";

  const columns = [
    { title: "Client", dataIndex: "clientName", key: "clientName" },
    { title: "Package", dataIndex: "packageName", key: "packageName" },
    {
      title: "Owed (tokens)",
      dataIndex: "outstanding",
      key: "outstanding",
      render: (v) => <strong>{Number(v || 0).toLocaleString()}</strong>,
    },
    {
      title: "Est. amount",
      dataIndex: "estimatedAmount",
      key: "estimatedAmount",
      render: (v, r) =>
        Number(v || 0) > 0 ? `${Number(v).toFixed(2)} ${r.currencyCode || "EUR"}` : "—",
    },
    {
      title: "Still drawable",
      dataIndex: "available",
      key: "available",
      render: (v) => Number(v || 0).toLocaleString(),
    },
    {
      title: "Due",
      dataIndex: "dueAtUtc",
      key: "dueAtUtc",
      render: (v, r) => (
        <span>
          {new Date(v).toLocaleDateString()}{" "}
          <Badge variant={r.isOverdue ? "error" : r.daysRemaining <= 3 ? "warning" : "neutral"}>
            {r.isOverdue
              ? `${Math.abs(r.daysRemaining)}d overdue`
              : `${r.daysRemaining}d left`}
          </Badge>
        </span>
      ),
    },
  ];

  return (
    <Collapse
      defaultActiveKey={overdue > 0 ? ["debts"] : []}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: "debts",
          label: (
            <span>
              <strong>Outstanding balances</strong> — {rows.length} client
              {rows.length === 1 ? "" : "s"}, {totalTokens.toLocaleString()} tokens
              {totalAmount > 0 ? ` (~${totalAmount.toFixed(2)} ${currency})` : ""}
              {/* Badge takes no style prop by design, so spacing lives on a wrapper. */}
              {overdue > 0 && (
                <span style={{ marginLeft: 8 }}>
                  <Badge variant="error">{overdue} blocked</Badge>
                </span>
              )}
            </span>
          ),
          children: (
            <Table
              rowKey="subscriptionId"
              size="small"
              columns={columns}
              dataSource={rows}
              pagination={rows.length > 8 ? { pageSize: 8, showSizeChanger: false } : false}
            />
          ),
        },
      ]}
    />
  );
};

const PaymentsList = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [outstanding, setOutstanding] = useState([]);
  const { mutate } = useMutations();
  const tableRef = useRef(null);
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  useEffect(() => {
    let cancelled = false;
    mutateRef.current("fetchOutstandingBalances", {
      method: "get",
      endpoint: "/payments/outstanding",
      onSuccess: (res) => {
        if (!cancelled) setOutstanding(res || []);
      },
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchData = useCallback(
    async (page, pageSize, search, f) => {
      let result = { data: [], total: 0 };

      await mutate("fetchPayments", {
        method: "get",
        endpoint: "/payments",
        params: {
          page,
          pageSize,
          search,
          startDate: f?.startDate ? f.startDate.toISOString() : undefined,
          endDate: f?.endDate ? f.endDate.toISOString() : undefined,
        },
        onSuccess: (res) => {
          result = {
            data: res?.items || [],
            total: res?.totalCount || 0,
          };
        },
      });

      return result;
    },
    [mutate]
  );

  const onPreviewInvoice = async (paymentId) => {
    try {
      const { data } = await mutate("getInvoiceByPayment", {
        method: "get",
        endpoint: `/invoices/by-payment/${paymentId}`,
      });
      const html = data?.data?.htmlContent;
      if (!html) {
        message.warning("No invoice found for this payment");
        return;
      }

      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch {
      message.error("Failed to load invoice");
    }
  };

  const columns = useMemo(
    () => [
      { title: "Client", dataIndex: "clientName", key: "clientName" },
      { title: "Package", dataIndex: "packageName", key: "packageName" },
      {
        title: "Type",
        dataIndex: "paymentType",
        key: "paymentType",
        render: (value) =>
          value === "TopUp" ? (
            <Badge variant="info">Top-Up</Badge>
          ) : (
            <Badge variant="neutral">Regular</Badge>
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => (
          <Badge variant={statusBadgeVariant(value)}>{value}</Badge>
        ),
      },
      {
        title: "Net",
        dataIndex: "amount",
        key: "amount",
        render: (value, record) =>
          `${Number(value || 0).toFixed(2)} ${record.currencyCode || "EUR"}`,
      },
      {
        title: "Total",
        dataIndex: "totalAmount",
        key: "totalAmount",
        render: (value, record) =>
          `${Number(value || 0).toFixed(2)} ${record.currencyCode || "EUR"}`,
      },
      {
        title: "Token Used",
        dataIndex: "tokenUsed",
        key: "tokenUsed",
      },
      {
        title: "From",
        dataIndex: "fromDate",
        key: "fromDate",
        render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
      },
      {
        title: "To",
        dataIndex: "toDate",
        key: "toDate",
        render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <>
            <Tooltip title="Edit">
              <Button
                variant="ghost"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/payments/${record.paymentId}/edit`, {
                    state: { record },
                  })
                }
              />
            </Tooltip>
            <Tooltip title="Invoice">
              <Button
                variant="ghost"
                icon={<FileTextOutlined />}
                onClick={() => onPreviewInvoice(record.paymentId)}
              />
            </Tooltip>
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Record and track client payments and their invoice status."
        extra={
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/payments/new")}
          >
            Create Payment
          </Button>
        }
      />
      <OutstandingBalances rows={outstanding} />
      <FilterBar filters={filters} onChange={setFilters} showDateRange />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        filters={filters}
        rowKey="paymentId"
      />

      <Modal
        title="Invoice Preview"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        width={900}
        footer={
          <>
            <Tooltip title="Print">
              <Button
                variant="secondary"
                icon={<PrinterOutlined />}
                onClick={() => {
                  const w = window.open("", "_blank");
                  w.document.write(previewHtml);
                  w.document.close();
                  w.print();
                }}
              >
                Print
              </Button>
            </Tooltip>
            <Button variant="secondary" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </>
        }
      >
        <div
          dangerouslySetInnerHTML={{ __html: previewHtml }}
          style={{
            border: "1px solid var(--border-subtle)",
            padding: 16,
            background: "var(--bg-surface)",
          }}
        />
      </Modal>
    </div>
  );
};

export default PaymentsList;
